/**
 * KPI カード(設計書 §2-3・§5-2)。主数値を大きく、目標・差分は小さく分けて主従を見せる。業務ロジックは持たない
 * (値の計算・目標の判定・文言は呼び出し側で作り、formatters で整えた文字列を渡す)。
 */
import type { ReactNode } from 'react';
import type { StatusTone } from './StatusBadge';

export default function MetricCard({
  label,
  value,
  unit,
  valueTone,
  status,
  actions,
  target,
  diff,
  diffTone = 'neutral',
  children,
  footnote,
  emphasis = false,
}: {
  label: ReactNode;
  /** 表示用に整えた主数値(例 "1,387") */
  value: ReactNode;
  unit?: ReactNode;
  /** 主数値の色(マイナスの金額など、意味があるときだけ) */
  valueTone?: StatusTone;
  /** 右上の状態(StatusBadge など) */
  status?: ReactNode;
  /** 見出しの右の切り替え(管理者込み/抜き など) */
  actions?: ReactNode;
  /** 例「目標 1,500件」 */
  target?: ReactNode;
  /** 例「目標比 +4.6pt」 */
  diff?: ReactNode;
  diffTone?: StatusTone;
  /** 補助の数値(項目名と値の行) */
  children?: ReactNode;
  footnote?: ReactNode;
  /** 特に見てほしいカード(現行シートの赤枠と同じ強調。発注者指示 2026-09-20) */
  emphasis?: boolean;
}) {
  return (
    <section className={`metric-card${emphasis ? ' is-key' : ''}`}>
      <div className="metric-card-head">
        <span className="metric-card-label">{label}</span>
        {actions}
        {status && <span className="metric-card-status">{status}</span>}
      </div>
      <div className="metric-card-value">
        <span className={`kpi-value${valueTone ? ` tone-${valueTone}` : ''}`}>{value}</span>
        {unit && <span className="kpi-unit">{unit}</span>}
      </div>
      {(target || diff) && (
        <div className="metric-card-sub">
          {target && <span className="metric-card-target">{target}</span>}
          {diff && <span className={`metric-card-diff tone-${diffTone}`}>{diff}</span>}
        </div>
      )}
      {children && <div className="metric-card-rows">{children}</div>}
      {footnote && <div className="metric-card-foot">{footnote}</div>}
    </section>
  );
}

/** カードの中の「項目名 … 値」の1行 */
export function MetricRow({ label, value, tone }: { label: ReactNode; value: ReactNode; tone?: StatusTone }) {
  return (
    <div className="metric-row">
      <span className="metric-row-label">{label}</span>
      <span className={`metric-row-value${tone ? ` tone-${tone}` : ''}`}>{value}</span>
    </div>
  );
}
